import { FormEvent, ReactNode, useEffect, useState } from "react";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import * as clientsApi from "../../api/clients";
import { getErrorMessage } from "../../api/client";
import { SERVIZI_PRECREDIT, SERVIZIO_PRECREDIT_LABELS, TipoServizioPrecredit } from "../../api/devices";
import { listResponsabili } from "../../api/statoLavori";
import type { DettaglioPratica, ModificaPratica, ModuloRecord, Passo, PersonaResponsabile } from "../../api/statoLavori";
import { dataIt, dispositivoAssociabile, oggiChiave, responsabilePreselezionato } from "./presentazione";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * Le finestre della scheda di una pratica. Ognuna riceve `onConferma`, che
 * esegue la scrittura e restituisce il messaggio d'errore del backend (o
 * `null` se è andata): la finestra resta aperta e lo mostra, così chi scrive
 * vede il perché senza perdere quello che ha inserito.
 */

export type Conferma<T> = (valori: T) => Promise<string | null>;

/** Lo scheletro comune: titolo, campi, errore, Annulla e conferma. */
function Finestra({
  titolo,
  onClose,
  onSubmit,
  etichettaConferma,
  disattiva = false,
  errore,
  inCorso,
  children,
}: {
  titolo: string;
  onClose: () => void;
  onSubmit: () => void;
  etichettaConferma: string;
  disattiva?: boolean;
  errore: string | null;
  inCorso: boolean;
  children: ReactNode;
}) {
  function invia(e: FormEvent) {
    e.preventDefault();
    if (!disattiva && !inCorso) onSubmit();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={titolo}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={inCorso}>
            Annulla
          </Button>
          <Button type="submit" form="finestra-pratica" loading={inCorso} disabled={disattiva}>
            {etichettaConferma}
          </Button>
        </>
      }
    >
      <form id="finestra-pratica" onSubmit={invia} className="flex flex-col gap-4">
        {children}
        {errore && (
          <p role="alert" className="rounded-lg bg-[#ef4444]/10 px-3 py-2 text-sm text-[#b91c1c]">
            {errore}
          </p>
        )}
      </form>
    </Modal>
  );
}

/** Esegue la conferma tenendo lo stato «in corso» e l'errore. */
function useInvio<T>(onConferma: Conferma<T>, onClose: () => void) {
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  async function invia(valori: T) {
    setInCorso(true);
    setErrore(null);
    try {
      const esito = await onConferma(valori);
      if (esito === null) onClose();
      else setErrore(esito);
    } finally {
      setInCorso(false);
    }
  }
  return { inCorso, errore, invia };
}

// ----------------------------------------------------------------------------

/**
 * Un giorno, e quando serve una nota o un valore: «Avanza a…» (il giorno dello
 * stato; per «Aperto» e «Assegnato» anche il conto tecnico o il codice GDO) e
 * le spunte (il giorno, con una nota facoltativa).
 */
export function FinestraGiorno({
  titolo,
  spiegazione,
  etichettaConferma,
  conNota = false,
  valore,
  onClose,
  onConferma,
}: {
  titolo: string;
  spiegazione?: ReactNode;
  etichettaConferma: string;
  conNota?: boolean;
  /** Il campo del valore (passi 3 e 4), con quello già salvato se c'è. */
  valore?: { etichetta: string; iniziale: string | null };
  onClose: () => void;
  onConferma: Conferma<{ il: string; nota: string; valore: string }>;
}) {
  const [il, setIl] = useState(oggiChiave());
  const [nota, setNota] = useState("");
  const [testo, setTesto] = useState(valore?.iniziale ?? "");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  // Senza un valore già salvato, «Aperto» e «Assegnato» lo chiedono: il
  // backend rifiuterebbe comunque (400), qui si evita il giro.
  const manca = !il || (valore !== undefined && !valore.iniziale && !testo.trim());
  return (
    <Finestra
      titolo={titolo}
      onClose={onClose}
      onSubmit={() => invia({ il, nota: nota.trim(), valore: testo.trim() })}
      etichettaConferma={etichettaConferma}
      disattiva={manca}
      errore={errore}
      inCorso={inCorso}
    >
      {spiegazione && <div className="text-sm text-ink/70">{spiegazione}</div>}
      <Input type="date" label="Il giorno" required value={il} onChange={(e) => setIl(e.target.value)} />
      {valore && (
        <Input
          label={valore.etichetta}
          required={!valore.iniziale}
          maxLength={50}
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          hint={valore.iniziale ? `Già salvato: ${valore.iniziale}. Cambialo solo se è sbagliato.` : undefined}
        />
      )}
      {conNota && (
        <Input
          label="Nota (facoltativa)"
          maxLength={255}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="es. a chi è andata l'email"
        />
      )}
    </Finestra>
  );
}

/** «Non necessario…»: il motivo è obbligatorio, e resta visibile sul passo (variante 5). */
export function FinestraMotivo({
  passo,
  onClose,
  onConferma,
}: {
  passo: Passo;
  onClose: () => void;
  onConferma: Conferma<string>;
}) {
  const [motivo, setMotivo] = useState("");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  return (
    <Finestra
      titolo="Segna come non necessario"
      onClose={onClose}
      onSubmit={() => invia(motivo.trim())}
      etichettaConferma="Conferma"
      disattiva={!motivo.trim()}
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">Passo: {passo.titolo}</p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="motivo-non-necessario" className="text-sm font-medium text-ink">
          Motivo <span className="text-accent">*</span>
        </label>
        <textarea
          id="motivo-non-necessario"
          rows={3}
          required
          maxLength={1000}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Spiega perché il passo non serve"
          className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-ink/40"
        />
      </div>
      <p className="text-xs text-ink/55">
        Il passo passa a «Non necessario» e si sblocca il successivo. Il motivo, chi l&apos;ha segnato e la data restano
        visibili sulla scheda.
      </p>
    </Finestra>
  );
}

/**
 * «Scarta» una bozza (PR C2): motivo obbligatorio. Lo scarto vale per quella
 * origine (quel sopralluogo, quell'installazione) finché non si annulla.
 */
export function FinestraScartaBozza({
  passo,
  onClose,
  onConferma,
}: {
  passo: Passo;
  onClose: () => void;
  onConferma: Conferma<string>;
}) {
  const [motivo, setMotivo] = useState("");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  return (
    <Finestra
      titolo="Scarta la bozza"
      onClose={onClose}
      onSubmit={() => invia(motivo.trim())}
      etichettaConferma="Scarta"
      disattiva={!motivo.trim()}
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">Passo: {passo.titolo}</p>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="motivo-scarto-bozza" className="text-sm font-medium text-ink">
          Motivo <span className="text-accent">*</span>
        </label>
        <textarea
          id="motivo-scarto-bozza"
          rows={3}
          required
          maxLength={1000}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Spiega perché la bozza non serve"
          className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-ink/40"
        />
      </div>
      <p className="text-xs text-ink/55">
        La bozza non torna finché non annulli lo scarto; ne nasce una nuova solo da un altro{" "}
        {passo.tipo === "installazione" ? "sopralluogo approvato" : "completamento dell'installazione"}.
        {passo.tipo === "attivazione" &&
          " Scartarla non spegne il badge «Attivazione da creare» dell'installazione: per quello si toglie la spunta «Da attivare il servizio Pre Credit»."}
      </p>
    </Finestra>
  );
}

/** «Correggi»: riporta il passo a uno stato già raggiunto, o a nessuno. Le righe tolte finiscono nell'audit. */
export function FinestraCorreggi({
  passo,
  onClose,
  onConferma,
}: {
  passo: Passo;
  onClose: () => void;
  onConferma: Conferma<string | null>;
}) {
  const raggiunti = passo.stati.filter((s) => s.raggiunto);
  // Si torna a uno stato prima del corrente: l'ultimo raggiunto è escluso.
  const scelte = [{ codice: null as string | null, etichetta: "Nessuno stato (Da fare)" }, ...raggiunti.slice(0, -1)];
  const [verso, setVerso] = useState<string | null>(scelte[scelte.length - 1].codice);
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  return (
    <Finestra
      titolo={`Correggi: ${passo.titolo}`}
      onClose={onClose}
      onSubmit={() => invia(verso)}
      etichettaConferma="Correggi"
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">
        Oggi il passo è a «{passo.stato_intermedio?.etichetta ?? "—"}». Scegli lo stato a cui riportarlo: quelli dopo
        vengono tolti, e la correzione resta nel registro attività.
      </p>
      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">Riporta il passo a</legend>
        {scelte.map((s) => (
          <label key={s.codice ?? "nessuno"} className="flex items-center gap-2 text-sm">
            <input type="radio" name="correggi-verso" checked={verso === s.codice} onChange={() => setVerso(s.codice)} />
            {s.etichetta}
          </label>
        ))}
      </fieldset>
    </Finestra>
  );
}

/** Un record candidato al collegamento: dalle attività delle sedi della cassaforte. */
interface Candidato {
  id: string;
  titolo: string;
  data: string | null;
  stato: string | null;
  sede: string;
}

const NOME_MODULO: Record<ModuloRecord, string> = {
  sopralluogo: "il sopralluogo",
  installazione: "l'installazione",
  attivazione: "l'attivazione",
};

/**
 * Collega a mano il record di un passo 6, 7 o 9: serve quando l'aggancio
 * automatico è ambiguo (più casseforti nella sede, record senza dispositivo).
 * I candidati sono i record delle sedi della cassaforte; per l'attivazione
 * solo la sede di questa pratica, perché dev'essere del suo cliente. Se il
 * record non va bene (altra cassaforte, già collegato altrove) lo dice il
 * backend, e la finestra mostra il suo messaggio.
 */
export function FinestraCollega({
  pratica,
  passo,
  onClose,
  onConferma,
}: {
  pratica: DettaglioPratica;
  passo: Passo;
  onClose: () => void;
  onConferma: Conferma<string | null>;
}) {
  const modulo = passo.tipo as ModuloRecord;
  const [candidati, setCandidati] = useState<Candidato[] | null>(null);
  const [erroreCarico, setErroreCarico] = useState<string | null>(null);
  const [scelto, setScelto] = useState<string | null>(passo.record?.id ?? null);
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);

  useEffect(() => {
    const sedi =
      modulo === "attivazione"
        ? [{ id: pratica.sede.id, nome: pratica.sede.nome }]
        : [{ id: pratica.sede.id, nome: pratica.sede.nome }, ...pratica.condivisa_con.map((c) => ({ id: c.sede.id, nome: c.sede.nome }))];
    const uniche = sedi.filter((s, i) => sedi.findIndex((x) => x.id === s.id) === i);
    let annullato = false;
    Promise.all(uniche.map((s) => clientsApi.getAttivitaSede(s.id).then((a) => ({ a, nome: s.nome ?? "Sede senza nome" }))))
      .then((elenchi) => {
        if (annullato) return;
        const righe: Candidato[] = elenchi.flatMap(({ a, nome }) =>
          modulo === "sopralluogo"
            ? a.sopralluoghi.map((r) => ({
                id: r.id,
                titolo: r.ragione_sociale_cliente ?? r.insegna_cliente ?? "Sopralluogo",
                data: r.data_sopralluogo,
                stato: r.stato,
                sede: nome,
              }))
            : modulo === "installazione"
              ? a.installazioni.map((r) => ({
                  id: r.id,
                  titolo: r.insegna ?? r.localita ?? "Installazione",
                  data: r.data_effettiva ?? r.ipotesi_data,
                  stato: r.stato,
                  sede: nome,
                }))
              : a.attivazioni.map((r) => ({
                  id: r.id,
                  titolo: r.cliente_nome ?? "Attivazione",
                  data: r.data_effettiva ?? r.data_attivazione,
                  stato: r.stato,
                  sede: nome,
                }))
        );
        setCandidati(righe);
      })
      .catch((err) => {
        if (!annullato) setErroreCarico(getErrorMessage(err, "Impossibile caricare i record della sede."));
      });
    return () => {
      annullato = true;
    };
  }, [modulo, pratica.sede.id, pratica.sede.nome, pratica.condivisa_con]);

  return (
    <Finestra
      titolo={`Collega ${NOME_MODULO[modulo]}`}
      onClose={onClose}
      onSubmit={() => invia(scelto)}
      etichettaConferma={scelto ? "Collega" : "Togli il collegamento"}
      disattiva={candidati === null}
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">
        Scegli {NOME_MODULO[modulo]} di questa cassaforte. Un record si collega a un passo solo: se è già collegato
        altrove, va prima scollegato da lì.
      </p>
      {erroreCarico ? (
        <p role="alert" className="text-sm text-[#b91c1c]">
          {erroreCarico}
        </p>
      ) : candidati === null ? (
        <p className="text-sm text-ink/50">Caricamento…</p>
      ) : (
        <fieldset className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          <legend className="sr-only">Record da collegare</legend>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="collega-record" className="mt-1" checked={scelto === null} onChange={() => setScelto(null)} />
            <span>Nessun collegamento a mano (vale l&apos;aggancio automatico)</span>
          </label>
          {candidati.length === 0 && <p className="text-sm text-ink/50">Nessun record presso le sedi della cassaforte.</p>}
          {candidati.map((c) => (
            <label key={c.id} className="flex items-start gap-2 text-sm">
              <input type="radio" name="collega-record" className="mt-1" checked={scelto === c.id} onChange={() => setScelto(c.id)} />
              <span>
                <span className="font-medium">{c.titolo}</span>
                <span className="block text-xs text-ink/55">
                  {[c.sede, c.data ? dataIt(c.data) : "senza data", c.stato].filter(Boolean).join(" · ")}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </Finestra>
  );
}

/**
 * Dati dell'ordine: «installare entro», interlocutore operativo e cartella.
 * Il referente del cliente e l'email del negozio sono solo suggerimenti: si
 * usano con un clic e si salvano solo con «Salva» (decisione del 22/10).
 * Partono solo i campi cambiati.
 */
export function FinestraDatiOrdine({
  pratica,
  onClose,
  onConferma,
}: {
  pratica: DettaglioPratica;
  onClose: () => void;
  onConferma: Conferma<ModificaPratica>;
}) {
  const d = pratica.dati_ordine;
  const iniziali = {
    installare_entro: d.installare_entro ?? "",
    interlocutore_operativo_nome: d.interlocutore.nome ?? "",
    interlocutore_operativo_email: d.interlocutore.email ?? "",
    interlocutore_operativo_telefono: d.interlocutore.telefono ?? "",
    cartella_url: d.cartella_url ?? "",
  };
  const [campi, setCampi] = useState(iniziali);
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  const cambiati: ModificaPratica = {};
  for (const k of Object.keys(iniziali) as (keyof typeof iniziali)[]) {
    if (campi[k].trim() !== iniziali[k]) cambiati[k] = campi[k].trim() || null;
  }
  const imposta = (patch: Partial<typeof campi>) => setCampi((c) => ({ ...c, ...patch }));
  const ref = d.suggerimenti_interlocutore.referente_cliente;
  const negozio = d.suggerimenti_interlocutore.email_negozio;

  return (
    <Finestra
      titolo="Dati dell'ordine"
      onClose={onClose}
      onSubmit={() => invia(cambiati)}
      etichettaConferma="Salva"
      disattiva={Object.keys(cambiati).length === 0}
      errore={errore}
      inCorso={inCorso}
    >
      <Input
        type="date"
        label="Installare entro"
        value={campi.installare_entro}
        onChange={(e) => imposta({ installare_entro: e.target.value })}
      />
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-sm font-medium text-ink">Interlocutore operativo</legend>
        {(ref || negozio) && (
          <div className="flex flex-col gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {ref && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  Suggerito dal referente del cliente: {[ref.nome, ref.email, ref.telefono].filter(Boolean).join(" · ")}. Da
                  confermare.
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    imposta({
                      interlocutore_operativo_nome: ref.nome ?? "",
                      interlocutore_operativo_email: ref.email ?? "",
                      interlocutore_operativo_telefono: ref.telefono ?? "",
                    })
                  }
                >
                  Usa questi dati
                </Button>
              </div>
            )}
            {negozio && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>Email del negozio: {negozio}. Da confermare.</span>
                <Button type="button" size="sm" variant="secondary" onClick={() => imposta({ interlocutore_operativo_email: negozio })}>
                  Usa questa email
                </Button>
              </div>
            )}
          </div>
        )}
        <Input label="Nome" maxLength={255} value={campi.interlocutore_operativo_nome} onChange={(e) => imposta({ interlocutore_operativo_nome: e.target.value })} />
        <Input label="Email" type="email" maxLength={255} value={campi.interlocutore_operativo_email} onChange={(e) => imposta({ interlocutore_operativo_email: e.target.value })} />
        <Input label="Telefono" type="tel" maxLength={50} value={campi.interlocutore_operativo_telefono} onChange={(e) => imposta({ interlocutore_operativo_telefono: e.target.value })} />
      </fieldset>
      <Input
        type="url"
        label="Cartella della pratica"
        maxLength={1000}
        placeholder="https://…"
        value={campi.cartella_url}
        onChange={(e) => imposta({ cartella_url: e.target.value })}
        hint="Solo il collegamento: l'app non crea e non legge la cartella."
      />
    </Finestra>
  );
}

/**
 * Il servizio della pratica (variante 2): scelto a mano, o preso dal
 * dispositivo. Dalla pratica non si scrive mai il servizio del dispositivo.
 */
export function FinestraServizio({
  iniziale,
  onClose,
  onConferma,
}: {
  iniziale: TipoServizioPrecredit | null;
  onClose: () => void;
  onConferma: Conferma<TipoServizioPrecredit | null>;
}) {
  const [servizio, setServizio] = useState<TipoServizioPrecredit | "">(iniziale ?? "");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  return (
    <Finestra
      titolo="Servizio della pratica"
      onClose={onClose}
      onSubmit={() => invia(servizio || null)}
      etichettaConferma="Salva"
      disattiva={(servizio || null) === iniziale}
      errore={errore}
      inCorso={inCorso}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor="servizio-pratica" className="text-sm font-medium text-ink">
          Servizio
        </label>
        <select
          id="servizio-pratica"
          value={servizio}
          onChange={(e) => setServizio(e.target.value as TipoServizioPrecredit | "")}
          className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
        >
          <option value="">Non indicato</option>
          {SERVIZI_PRECREDIT.map((s) => (
            <option key={s} value={s}>
              {SERVIZIO_PRECREDIT_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-ink/55">Cambia solo la pratica: il servizio del dispositivo resta com&apos;è.</p>
    </Finestra>
  );
}

/**
 * Il responsabile di un passo: un admin o uno staff attivo, oppure nessuno. Sui
 * passi della cassaforte (il 4) la riga è in comune: il responsabile vale per
 * tutte le sue pratiche.
 */
export function FinestraResponsabile({
  passo,
  onClose,
  onConferma,
}: {
  passo: Passo;
  onClose: () => void;
  onConferma: Conferma<string | null>;
}) {
  const [persone, setPersone] = useState<PersonaResponsabile[] | null>(null);
  const [erroreCarico, setErroreCarico] = useState<string | null>(null);
  /** `undefined` = ancora da scegliere; `null` = nessun responsabile. */
  const [scelto, setScelto] = useState<string | null | undefined>(undefined);
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);

  useEffect(() => {
    let annullato = false;
    listResponsabili()
      .then((elenco) => {
        if (annullato) return;
        setPersone(elenco);
        setScelto(responsabilePreselezionato(passo.responsabile, elenco));
      })
      .catch((err) => {
        if (!annullato) setErroreCarico(getErrorMessage(err, "Impossibile caricare le persone."));
      });
    return () => {
      annullato = true;
    };
  }, [passo.responsabile]);

  return (
    <Finestra
      titolo={`Responsabile: ${passo.titolo}`}
      onClose={onClose}
      onSubmit={() => {
        if (scelto !== undefined) void invia(scelto);
      }}
      etichettaConferma="Salva"
      disattiva={persone === null || scelto === undefined}
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">
        Oggi: {passo.responsabile?.nome ?? "nessun responsabile"}.
        {passo.ambito === "cassaforte" && " Il passo è della cassaforte: il responsabile vale per tutte le sue pratiche."}
      </p>
      {erroreCarico ? (
        <p role="alert" className="text-sm text-[#b91c1c]">
          {erroreCarico}
        </p>
      ) : persone === null ? (
        <p className="text-sm text-ink/50">Caricamento…</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="responsabile-passo" className="text-sm font-medium text-ink">
            Responsabile
          </label>
          <select
            id="responsabile-passo"
            value={scelto === undefined ? "" : (scelto ?? "nessuno")}
            onChange={(e) => setScelto(e.target.value === "nessuno" ? null : e.target.value)}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            {scelto === undefined && (
              <option value="" disabled>
                Scegli la persona
              </option>
            )}
            <option value="nessuno">Nessun responsabile</option>
            {persone.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
          <p className="text-xs text-ink/55">Si scelgono solo admin e staff attivi.</p>
        </div>
      )}
    </Finestra>
  );
}

/** «Chiudi pratica…»: il motivo è obbligatorio e resta visibile sulla scheda. */
/**
 * «Sede già attiva» (PR E1): conferma con l'elenco dei passi che diventano «non
 * necessario». Il passo 6 è della cassaforte: se è condivisa, lo si dice.
 */
export function FinestraSedeGiaAttiva({
  passi,
  condivisaCon,
  onClose,
  onConferma,
}: {
  /** I passi che verranno segnati, nell'ordine della scheda. */
  passi: Passo[];
  condivisaCon: DettaglioPratica["condivisa_con"];
  onClose: () => void;
  onConferma: Conferma<void>;
}) {
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  const sei = passi.find((p) => p.tipo === "sopralluogo");
  return (
    <Finestra
      titolo="Sede già attiva"
      onClose={onClose}
      onSubmit={() => invia(undefined)}
      etichettaConferma={passi.length === 1 ? "Segna il passo" : `Segna ${passi.length} passi`}
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">
        Installazione e attivazione ci sono già: i passi preliminari ancora aperti diventano «non necessario», con il motivo «Sede già
        attiva».
      </p>
      <ul className="list-disc space-y-0.5 pl-5 text-sm text-ink">
        {passi.map((p) => (
          <li key={p.tipo}>
            {p.numero}. {p.titolo}
          </li>
        ))}
      </ul>
      {sei && condivisaCon.length > 0 && (
        <p role="note" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Il passo {sei.numero} «{sei.titolo}» è della cassaforte, condivisa con{" "}
          {condivisaCon.map((c) => `${c.cliente.nome}${c.sede.nome ? ` – ${c.sede.nome}` : ""}`).join(", ")}: diventa «non necessario»
          anche {condivisaCon.length === 1 ? "nella sua pratica" : "nelle loro pratiche"}.
        </p>
      )}
      <p className="text-xs text-ink/55">
        I passi completati non cambiano, e nemmeno 3, 4, 7 e 9. Ogni passo si riapre dal suo pulsante «Annulla "non necessario"».
      </p>
    </Finestra>
  );
}

export function FinestraChiudiPratica({ onClose, onConferma }: { onClose: () => void; onConferma: Conferma<string> }) {
  const [motivo, setMotivo] = useState("");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  return (
    <Finestra
      titolo="Chiudi la pratica"
      onClose={onClose}
      onSubmit={() => invia(motivo.trim())}
      etichettaConferma="Chiudi la pratica"
      disattiva={!motivo.trim()}
      errore={errore}
      inCorso={inCorso}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor="motivo-chiusura" className="text-sm font-medium text-ink">
          Motivo <span className="text-accent">*</span>
        </label>
        <textarea
          id="motivo-chiusura"
          rows={3}
          required
          maxLength={1000}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="es. il cliente ha rinunciato"
          className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-ink/40"
        />
      </div>
      <p className="text-xs text-ink/55">
        Una pratica chiusa esce dall&apos;elenco «In corso» e non si modifica più, finché qualcuno non la riapre. Le altre
        pratiche della stessa cassaforte restano come sono.
      </p>
    </Finestra>
  );
}

/**
 * «Chiudi cassaforte…» e «Riapri cassaforte…»: tutte e due col motivo
 * obbligatorio, che il backend scrive nell'audit. Valgono per tutte le pratiche
 * della cassaforte, quindi la finestra dice quali altre tocca.
 */
export function FinestraCassaforte({
  azione,
  condivisaCon,
  onClose,
  onConferma,
}: {
  azione: "chiudi" | "riapri";
  condivisaCon: DettaglioPratica["condivisa_con"];
  onClose: () => void;
  onConferma: Conferma<string>;
}) {
  const [motivo, setMotivo] = useState("");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);
  const chiudi = azione === "chiudi";
  const titolo = chiudi ? "Chiudi la cassaforte" : "Riapri la cassaforte";
  const altre = condivisaCon.map((c) => `${c.cliente.nome}${c.sede.nome ? ` – ${c.sede.nome}` : ""}`).join(", ");
  return (
    <Finestra
      titolo={titolo}
      onClose={onClose}
      onSubmit={() => invia(motivo.trim())}
      etichettaConferma={titolo}
      disattiva={!motivo.trim()}
      errore={errore}
      inCorso={inCorso}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor="motivo-cassaforte" className="text-sm font-medium text-ink">
          Motivo <span className="text-accent">*</span>
        </label>
        <textarea
          id="motivo-cassaforte"
          rows={3}
          required
          maxLength={1000}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder={chiudi ? "es. il posto non va bene e non si rifà il sopralluogo" : "es. trovato un altro posto per la cassaforte"}
          className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-ink/40"
        />
      </div>
      {condivisaCon.length > 0 && (
        <p role="note" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          La cassaforte è condivisa con {altre}: {chiudi ? "si chiude" : "si riapre"} anche{" "}
          {condivisaCon.length === 1 ? "la sua pratica" : "le loro pratiche"}.
        </p>
      )}
      <p className="text-xs text-ink/55">
        {chiudi
          ? "Le pratiche della cassaforte escono dall'elenco «In corso» e dal «Da fare», e non si modificano più finché qualcuno non la riapre. Nessun passo viene modificato; le bozze da confermare decadono, e il passo che le mostrava torna «Da fare»."
          : "Le pratiche tornano modificabili, con i passi com'erano, e le bozze ancora dovute si ripropongono. Una pratica chiusa da sola resta chiusa: si riapre dalla sua scheda."}
      </p>
    </Finestra>
  );
}

/**
 * «Associa dispositivo…»: il device della cassaforte, quando c'è (dopo
 * l'installazione). Si propongono i dispositivi con una relazione aperta nella
 * sede della pratica, e fra questi solo quelli di tipo Cassaforte o non
 * classificati. Un device già di un'altra cassaforte lo rifiuta il backend.
 */
export function FinestraDispositivo({
  pratica,
  onClose,
  onConferma,
}: {
  pratica: DettaglioPratica;
  onClose: () => void;
  onConferma: Conferma<string>;
}) {
  const [dispositivi, setDispositivi] = useState<clientsApi.DispositivoDiSede[] | null>(null);
  const [erroreCarico, setErroreCarico] = useState<string | null>(null);
  const [scelto, setScelto] = useState("");
  const { inCorso, errore, invia } = useInvio(onConferma, onClose);

  useEffect(() => {
    let annullato = false;
    clientsApi
      .getAttivitaSede(pratica.sede.id)
      .then((a) => {
        if (annullato) return;
        setDispositivi(a.dispositivi);
        const associabili = a.dispositivi.filter((d) => dispositivoAssociabile(d.tipo_dispositivo));
        if (associabili.length === 1) setScelto(associabili[0].id);
      })
      .catch((err) => {
        if (!annullato) setErroreCarico(getErrorMessage(err, "Impossibile caricare i dispositivi della sede."));
      });
    return () => {
      annullato = true;
    };
  }, [pratica.sede.id]);

  const associabili = (dispositivi ?? []).filter((d) => dispositivoAssociabile(d.tipo_dispositivo));
  const esclusi = (dispositivi?.length ?? 0) - associabili.length;
  return (
    <Finestra
      titolo="Associa il dispositivo alla cassaforte"
      onClose={onClose}
      onSubmit={() => invia(scelto)}
      etichettaConferma="Associa"
      disattiva={!scelto}
      errore={errore}
      inCorso={inCorso}
    >
      <p className="text-sm text-ink/70">
        Cassaforte: {pratica.cassaforte.etichetta ?? "senza etichetta"}, presso {pratica.sede.nome ?? "sede senza nome"}.
        {pratica.condivisa_con.length > 0 && " La cassaforte è condivisa: il dispositivo vale per tutte le sue pratiche."}
      </p>
      {erroreCarico ? (
        <p role="alert" className="text-sm text-[#b91c1c]">
          {erroreCarico}
        </p>
      ) : dispositivi === null ? (
        <p className="text-sm text-ink/50">Caricamento…</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="dispositivo-cassaforte" className="text-sm font-medium text-ink">
            Dispositivo presso la sede
          </label>
          <select
            id="dispositivo-cassaforte"
            value={scelto}
            disabled={associabili.length === 0}
            onChange={(e) => setScelto(e.target.value)}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">{associabili.length === 0 ? "Nessuna cassaforte presso la sede" : "Scegli il dispositivo"}</option>
            {associabili.map((d) => (
              <option key={d.id} value={d.id}>
                {[etichettaMatricola(d.matricola), d.tipo_dispositivo === null ? "non classificato" : null, d.stato !== "attivo" ? d.stato : null]
                  .filter(Boolean)
                  .join(" · ")}
              </option>
            ))}
          </select>
          {esclusi > 0 && (
            <p className="text-xs text-ink/55">
              {esclusi === 1
                ? "Un dispositivo di altro tipo (rendiresto, TVCC…) non si propone: non è una cassaforte."
                : `${esclusi} dispositivi di altro tipo (rendiresto, TVCC…) non si propongono: non sono casseforti.`}
            </p>
          )}
        </div>
      )}
      <p className="text-xs text-ink/55">
        Controlla la matricola: una volta associato, il dispositivo non si cambia e non si toglie.
      </p>
    </Finestra>
  );
}
